import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  signal } from '@angular/core';
import { base64Decode, base64Encode, Store } from '@softline/core';
import {
  HardwareManagementStore,
  SOFTLINE_FEATURE_HARDWARE_MANAGEMENT,
  SOFTLINE_PERMISSION_HARDWARE_MANAGEMENT_EDITING } from '../../hardware-management.shared';
import { BehaviorSubject, iif, Observable, Subscription, switchMap } from 'rxjs';
import { Hardware } from '../../types/hardware';
import { ActivatedRoute, Router } from '@angular/router';
import { map } from 'rxjs/operators';
import {
  BackNavigationService,
  Command,
  CommandStore,
  Refreshable,
  RefreshService,
  showRequestErrors,
  SOFTLINE_FEATURE_COMMANDS } from '@softline/application';
import {
  MessageBarStore,
  ModalStore,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  SOFTLINE_FEATURE_MODAL } from '@softline/ui-core';
import { authorizationFn } from '@softline/auth';
import { HardwarePropertyNameMap } from '../../data/hardware-property-name-map';
import { CommonModule } from '@angular/common';
import { HardwareDetailsComponent } from './hardware-details/hardware-details.component';
import { MasterDetailPageNew, PageHeaderComponent } from '@softapps/core/pages';
import { toObservable } from '@angular/core/rxjs-interop';

export interface DisplayableQuery {
  key: string;
  name: string;
  value: string | number;
}

import { ChipComponent } from '@softline/ui-core';
import { ChipGroupComponent } from '@softline/ui-core';
import { InputComponent } from '@softline/ui-core';
import { MasterDetailComponent } from '@softline/ui-core/data-view';
import { SortPipe } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
  selector: 'lib-results',
  imports: [
    CommonModule,
    HardwareDetailsComponent,
    MasterDetailPageNew,
    PageHeaderComponent,
    ChipComponent,
    ChipGroupComponent,
    InputComponent,
    MasterDetailComponent,
    SortPipe,
    TranslatePipe],
  templateUrl: './results.component.html',
  styleUrls: ['./results.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush })
export class ResultsComponent implements OnInit, OnDestroy, Refreshable {
  private querySubscription?: Subscription;

  sort$ = new BehaviorSubject<'ASC' | 'DESC'>('DESC');
  query$ = new BehaviorSubject<DisplayableQuery[]>([]);

  readonly userInitiatedChange = signal(false);

  readonly idFunction = (hardware: Hardware) => hardware.autoid + '';

  private readonly hardwareEntities$ = this.store.observe(
    SOFTLINE_FEATURE_HARDWARE_MANAGEMENT,
    HardwareManagementStore.getters.entities
  );

  private readonly allHardware$ = this.store.observe(
    SOFTLINE_FEATURE_HARDWARE_MANAGEMENT,
    HardwareManagementStore.getters.all
  );

  readonly searchText$ = new BehaviorSubject<string>('');

  readonly hardwareData$: Observable<readonly Hardware[]> = this.searchText$.pipe(
    switchMap((searchText) =>
      iif(
        () => searchText === '',
        this.allHardware$,
        this.allHardware$.pipe(
          map((hardware) => this.filterHardware(hardware, searchText))
        )
      )
    )
  );

  readonly selectedHardware = signal<Hardware | null>(null);
  readonly selectedHardware$ = toObservable(this.selectedHardware);

  constructor(
    private store: Store,
    protected router: Router,
    private activatedRoute: ActivatedRoute,
    private refreshService: RefreshService,
    private backNavigationService: BackNavigationService
  ) {}

  async ngOnInit(): Promise<void> {
    this.refreshService.add(this);

    this.store.commit(SOFTLINE_FEATURE_COMMANDS, CommandStore.mutations.addSet, {
      name: ResultsComponent,
      commands: this.createCommands() });

    this.querySubscription = this.activatedRoute.queryParamMap.subscribe((o) => {
      const encodedParams = o.get('query');
      const query = encodedParams ? JSON.parse(base64Decode(encodedParams)) : {};
      this.query$.next(this.convertToDisplayableQuery(query));
      this.load(query);
    });
  }

  ngOnDestroy(): void {
    if (this.querySubscription && !this.querySubscription.closed)
      this.querySubscription.unsubscribe();
    this.querySubscription = undefined;

    this.refreshService.remove(this);
    this.store.commit(
      SOFTLINE_FEATURE_COMMANDS,
      CommandStore.mutations.removeSet,
      ResultsComponent
    );
  }

  async refresh(): Promise<void> {
    const encodedParams = this.activatedRoute.snapshot.queryParamMap.get('query');
    const query = encodedParams ? JSON.parse(base64Decode(encodedParams)) : {};
    await this.load(query);
  }

  private async load(query: Partial<Hardware>): Promise<void> {
    try {
      const queryParams = Object.keys(query)?.length > 0 ? { query } : undefined;
      await this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT,
        HardwareManagementStore.actions.loadMany,
        { clear: true, queryParams }
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  private readonly isAuthorized = authorizationFn();
  private readonly hasEditingPermission$ = toObservable(
    this.isAuthorized(SOFTLINE_PERMISSION_HARDWARE_MANAGEMENT_EDITING)
  );

  protected createCommands(): Command[] {
    return [
      {
        name: 'Löschen',
        icon: 'fa-regular fa-trash',
        class: 'menu action-menu action-menu-top',
        canExecute: this.selectedHardware$.pipe(map((o) => !!o)),
        isVisible: this.hasEditingPermission$,
        execute: () => this.deleteSelected() }];
  }

  async deleteSelected(): Promise<void> {
    const hardware = this.selectedHardware();
    if (!hardware) return;

    const result = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.ask,
      {
        title: 'Löschen',
        question:
          'Sind Sie sicher, dass die Hardware unwiderruflich gelöscht werden soll?' }
    );

    if (!result || result !== 'YES') {
      return;
    }

    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT,
        HardwareManagementStore.actions.delete,
        { entity: hardware }
      );

      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        'Hardware wurde erfolgreich gelöscht'
      );

      await this.router.navigate(['/hardware-management']);
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  async removeQuery(key: keyof Hardware | string): Promise<void> {
    const params = this.activatedRoute.snapshot.queryParams;
    const newQueryParams = JSON.parse(base64Decode(params['query']) ?? '{}') ?? {};

    if (newQueryParams[key]) {
      delete newQueryParams[key];
    }

    if (Object.keys(newQueryParams).length < 1) {
      await this.router.navigate([]);
    } else {
      await this.router.navigate([], {
        queryParams: { query: base64Encode(JSON.stringify(newQueryParams)) } });
    }
  }

  async selectionChanged(hardware: Hardware | null | undefined): Promise<void> {
    /* await this.router.navigate([{ id: hardware?.id }], {
      queryParamsHandling: 'preserve' }); */
    this.userInitiatedChange.set(true);
  }

  private convertToDisplayableQuery(query: Partial<Hardware>): DisplayableQuery[] {
    const displayableQueries: DisplayableQuery[] = [];

    for (const [key, value] of Object.entries(query)) {
      displayableQueries.push({
        key,
        value: HardwarePropertyNameMap[key].convert(value),
        name: HardwarePropertyNameMap[key].name(key) });
    }

    return displayableQueries;
  }

  private filterHardware(hardware: readonly Hardware[], searchText: string): Hardware[] {
    if (searchText === '') {
      return hardware as Hardware[];
    }

    const query = searchText.toLowerCase();

    return hardware.filter((item) => {
      return (
        item.id.toString().includes(query) ||
        ('' + item?.autoid).toLowerCase().includes(query) ||
        item?.hostname?.toLowerCase()?.includes(query) ||
        item?.benutzer?.toLowerCase()?.includes(query) ||
        item?.seriennummer?.toLowerCase()?.includes(query) ||
        item?.produkt?.bezeichnung?.toLowerCase()?.includes(query) ||
        item?.hersteller?.hersteller?.toLowerCase()?.includes(query) ||
        item?.hersteller?.bezeichnung?.toLowerCase()?.includes(query)
      );
    });
  }
}
